package com.evoapps.evoshift.domain.team

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.timeline.EmployeeTimeline
import com.evoapps.evoshift.domain.timeline.TimelineEdit
import com.evoapps.evoshift.domain.timeline.TimelineEntry
import com.evoapps.evoshift.domain.timeline.TimelineKind
import com.evoapps.evoshift.domain.timeline.TimelineLane
import kotlinx.datetime.LocalDate
import kotlin.math.round

/*
 * SH-269: changing several people at once (the Team screen's "Edit selected"): only the fields
 * chosen change, everything else stays as each person has it.
 * - Pay (a raise by an amount or a percentage) and the contract (type, declared hours) are
 *   effective-dated entries of the employee timeline (SH-212) from a day: never an overwrite, so
 *   past months and the frozen shift-cost snapshots stay as they are. Back-dating is allowed, but
 *   not into (or across) a month whose roster is LOCKED or ARCHIVED (owner, 2026-10-30).
 * - The end of employment is the timeline's END entry (a future day is a planned end).
 * - The position and skills are the person's engagement at the workplace, as the employee form
 *   saves them.
 * Pay, contract and end (the timeline's) are the owner's and financial managers' (MANAGE_PAY);
 * position and skills every manager's (MANAGE_TEAM), each at their own workplaces.
 */

/** A raise of the pay in force on [from]: by [value] zł (AMOUNT) or by [value] percent (PERCENT); negative lowers it. */
data class PayRaise(val kind: Kind, val value: Double, val from: LocalDate) {
    enum class Kind { AMOUNT, PERCENT }

    /** A change at all, and not a cut of 100% or more. */
    val isValid: Boolean get() = value.isFinite() && value != 0.0 && (kind == Kind.AMOUNT || value > -100.0)

    /** [pay] with the raise (to the grosz, never below zero); the stored employer cost is not used (computed). */
    fun applyTo(pay: CompensationModel): CompensationModel = when (pay) {
        is CompensationModel.Hourly -> pay.copy(rate = raise(pay.rate))
        is CompensationModel.MonthlyFixed -> pay.copy(baseSalary = raise(pay.baseSalary))
    }

    private fun raise(amount: Double): Double {
        val raised = if (kind == Kind.AMOUNT) amount + value else amount * (1 + value / 100.0)
        return (round(raised * 100) / 100).coerceAtLeast(0.0)
    }
}

/** The skills change: some added, some removed, or the list replaced. */
sealed interface SkillsEdit {
    fun applyTo(skills: List<String>): List<String>

    data class Add(val skills: Set<String>) : SkillsEdit {
        override fun applyTo(skills: List<String>) = skills + this.skills.filter { it !in skills }.sorted()
    }

    data class Remove(val skills: Set<String>) : SkillsEdit {
        override fun applyTo(skills: List<String>) = skills.filter { it !in this.skills }
    }

    data class Replace(val skills: List<String>) : SkillsEdit {
        override fun applyTo(skills: List<String>) = this.skills.distinct()
    }

    /** Some removed, then others added (the sheet's two lists). */
    data class Change(val add: Set<String>, val remove: Set<String>) : SkillsEdit {
        override fun applyTo(skills: List<String>) = Add(add).applyTo(Remove(remove).applyTo(skills))
    }
}

/** The contract from [from]: a new type and / or declared monthly hours (null: as it is). */
data class ContractEdit(val contractTypeId: String? = null, val declaredHours: Int? = null, val from: LocalDate) {
    val isEmpty: Boolean get() = contractTypeId == null && declaredHours == null
}

/** What "Edit selected" changes; null fields stay as each person has them. */
data class BulkEmployeeEdit(
    val payRaise: PayRaise? = null,
    val role: String? = null,
    val skills: SkillsEdit? = null,
    val endOn: LocalDate? = null,
    val contract: ContractEdit? = null
) {
    val fields: Set<BulkField>
        get() = buildSet {
            if (payRaise != null) add(BulkField.PAY)
            if (contract != null && !contract.isEmpty) add(BulkField.CONTRACT)
            if (endOn != null) add(BulkField.END)
            if (!role.isNullOrBlank()) add(BulkField.ROLE)
            if (skills != null) add(BulkField.SKILLS)
        }

    val isEmpty: Boolean get() = fields.isEmpty()
}

enum class BulkField {
    PAY, CONTRACT, END, ROLE, SKILLS;

    /** Kept in the employee timeline (effective-dated): the owner's and financial managers'. */
    val viaTimeline: Boolean get() = this == PAY || this == CONTRACT || this == END

    /** What the editor needs at the person's workplace. */
    val needs: Capability get() = if (viaTimeline) Capability.MANAGE_PAY else Capability.MANAGE_TEAM
}

/** One person at one workplace, as the bulk change sees them. */
data class BulkTarget(
    val userId: String,
    val name: String,
    val workplaceId: String,
    val workplaceName: String,
    val engagement: Engagement,
    /** Their pay terms there; null: none (or not readable for the editor). */
    val financial: FinancialEngagement? = null
)

/** One field's change for one person: the facts the summary shows ("from … to …"). */
sealed interface BulkChange {
    val field: BulkField

    data class Pay(val before: CompensationModel, val after: CompensationModel, val from: LocalDate) : BulkChange {
        override val field get() = BulkField.PAY
    }

    data class Contract(
        val beforeType: String,
        val afterType: String,
        val beforeHours: Int?,
        val afterHours: Int?,
        val from: LocalDate
    ) : BulkChange {
        override val field get() = BulkField.CONTRACT
    }

    /** [before]: the end in force ("YYYY-MM-DD"), none: null. */
    data class End(val before: String?, val after: LocalDate) : BulkChange {
        override val field get() = BulkField.END
    }

    data class Role(val before: String?, val after: String) : BulkChange {
        override val field get() = BulkField.ROLE
    }

    data class Skills(val before: List<String>, val after: List<String>) : BulkChange {
        override val field get() = BulkField.SKILLS
    }
}

/** Why a field does not change for a person. */
enum class BulkSkip {
    /** The editor may not change it at this workplace. */
    NOT_ALLOWED,
    /** Pay or contract: no pay terms at the workplace. */
    NO_PAY_TERMS,
    /** The day falls in (or before) a month whose roster is locked or archived. */
    MONTH_CLOSED,
    /** The day is before the person started at the workplace. */
    BEFORE_START,
    /** End of employment: the person no longer works there. */
    ALREADY_ENDED,
    /** It is already so. */
    NO_CHANGE
}

/** The plan for one person at one workplace: what changes and what is skipped (and why). */
data class BulkLine(val target: BulkTarget, val changes: List<BulkChange>, val skipped: Map<BulkField, BulkSkip>) {
    val changesAnything: Boolean get() = changes.isNotEmpty()
    fun change(field: BulkField): BulkChange? = changes.firstOrNull { it.field == field }
}

/** The summary before saving: "N people, M workplaces", line by line. */
data class BulkPlan(val edit: BulkEmployeeEdit, val lines: List<BulkLine>) {
    val changing: List<BulkLine> get() = lines.filter { it.changesAnything }
    val people: Int get() = changing.map { it.target.userId }.distinct().size
    val workplaces: Int get() = changing.map { it.target.workplaceId }.distinct().size
    val isEmpty: Boolean get() = changing.isEmpty()
}

/** SH-269: the plan of a bulk change, without writing anything. */
object BulkEditPlanner {
    /**
     * [edit] for each of [targets]: [rights] is what the editor may do at a workplace;
     * [closedMonths] the months ("YYYY-MM") whose roster is LOCKED or ARCHIVED there.
     */
    fun plan(
        edit: BulkEmployeeEdit,
        targets: List<BulkTarget>,
        rights: (workplaceId: String) -> Set<Capability>,
        closedMonths: (workplaceId: String) -> Set<String>
    ): BulkPlan = BulkPlan(edit, targets.map { line(edit, it, rights(it.workplaceId), closedMonths(it.workplaceId)) })

    private fun line(edit: BulkEmployeeEdit, target: BulkTarget, rights: Set<Capability>, closed: Set<String>): BulkLine {
        val changes = mutableListOf<BulkChange>()
        val skipped = mutableMapOf<BulkField, BulkSkip>()
        val engagement = target.engagement
        fun dated(field: BulkField, day: LocalDate): BulkSkip? = when {
            field.needs !in rights || Capability.MAKE_CHANGES !in rights -> BulkSkip.NOT_ALLOWED
            closes(day, closed) -> BulkSkip.MONTH_CLOSED
            engagement.startDate.length >= 10 && day.toString() < engagement.startDate.take(10) -> BulkSkip.BEFORE_START
            else -> null
        }
        edit.fields.forEach { field ->
            val result: Any = when (field) {
                BulkField.PAY -> {
                    val raise = edit.payRaise!!
                    val financial = target.financial
                    dated(field, raise.from) ?: if (financial == null) BulkSkip.NO_PAY_TERMS else {
                        val before = financial.compensationOn(raise.from.toString())
                        val after = raise.applyTo(before)
                        if (after == before) BulkSkip.NO_CHANGE else BulkChange.Pay(before, after, raise.from)
                    }
                }
                BulkField.CONTRACT -> {
                    val contract = edit.contract!!
                    val financial = target.financial
                    dated(field, contract.from) ?: if (financial == null) BulkSkip.NO_PAY_TERMS else {
                        val before = financial.contractOn(contract.from.toString())
                        val type = contract.contractTypeId ?: before.contractTypeId
                        val hours = contract.declaredHours ?: before.declaredNumberOfHours
                        if (type == before.contractTypeId && hours == before.declaredNumberOfHours) BulkSkip.NO_CHANGE
                        else BulkChange.Contract(before.contractTypeId, type, before.declaredNumberOfHours, hours, contract.from)
                    }
                }
                BulkField.END -> {
                    val day = edit.endOn!!
                    dated(field, day) ?: when {
                        !engagement.isActive -> BulkSkip.ALREADY_ENDED
                        engagement.endDate?.take(10) == day.toString() -> BulkSkip.NO_CHANGE
                        else -> BulkChange.End(engagement.endDate?.take(10), day)
                    }
                }
                BulkField.ROLE -> {
                    val role = edit.role!!.trim()
                    when {
                        field.needs !in rights || Capability.MAKE_CHANGES !in rights -> BulkSkip.NOT_ALLOWED
                        engagement.role == role -> BulkSkip.NO_CHANGE
                        else -> BulkChange.Role(engagement.role, role)
                    }
                }
                BulkField.SKILLS -> {
                    val after = edit.skills!!.applyTo(engagement.skills)
                    when {
                        field.needs !in rights || Capability.MAKE_CHANGES !in rights -> BulkSkip.NOT_ALLOWED
                        after == engagement.skills -> BulkSkip.NO_CHANGE
                        else -> BulkChange.Skills(engagement.skills, after)
                    }
                }
            }
            when (result) {
                is BulkChange -> changes += result
                is BulkSkip -> skipped[field] = result
            }
        }
        return BulkLine(target, changes.sortedBy { it.field.ordinal }, skipped)
    }

    /** Whether a change from [day] would reach a closed month: its own, or any later one. */
    fun closes(day: LocalDate, closed: Set<String>): Boolean {
        val month = day.toString().take(7)
        return closed.any { it >= month }
    }
}

/**
 * SH-269: a bulk line as edits of the person's timeline: a new entry from the day, or the one
 * starting that very day replaced; the end of employment moved when there is one. [inverse] is the
 * Undo of edits applied to [EmployeeTimeline] `before` (newest first).
 */
object BulkTimelineEdits {
    fun edits(timeline: EmployeeTimeline, edit: BulkEmployeeEdit, line: BulkLine): List<TimelineEdit> = buildList {
        (line.change(BulkField.CONTRACT) as? BulkChange.Contract)?.let { change ->
            val current = inForce(timeline, TimelineKind.CONTRACT, change.from)
            val entry = TimelineEntry(
                TimelineKind.CONTRACT, change.from,
                contractTypeId = edit.contract?.contractTypeId ?: current?.contractTypeId ?: change.afterType,
                declaredHours = edit.contract?.declaredHours ?: current?.declaredHours
            )
            add(addOrReplace(timeline, entry))
        }
        (line.change(BulkField.PAY) as? BulkChange.Pay)?.let { change ->
            val raise = edit.payRaise ?: return@let
            val pay = inForce(timeline, TimelineKind.PAY, change.from)?.pay ?: change.before
            add(addOrReplace(timeline, TimelineEntry(TimelineKind.PAY, change.from, pay = raise.applyTo(pay))))
        }
        (line.change(BulkField.END) as? BulkChange.End)?.let { change ->
            val entry = TimelineEntry(TimelineKind.END, change.after)
            add(if (timeline.end != null) TimelineEdit.Replace(TimelineKind.END.name, entry) else TimelineEdit.Add(entry))
        }
    }

    fun inverse(before: EmployeeTimeline, applied: List<TimelineEdit>): List<TimelineEdit> = applied.reversed().mapNotNull { edit ->
        when (edit) {
            is TimelineEdit.Add -> TimelineEdit.Remove(edit.entry.id)
            is TimelineEdit.Replace -> before.entry(edit.id)?.let { TimelineEdit.Replace(edit.entry.id, it) }
            is TimelineEdit.Remove -> before.entry(edit.id)?.let { TimelineEdit.Add(it) }
        }
    }

    private fun inForce(timeline: EmployeeTimeline, kind: TimelineKind, day: LocalDate): TimelineEntry? =
        timeline.entriesOf(TimelineLane(kind)).lastOrNull { it.from == null || it.from <= day }
            ?: timeline.entriesOf(TimelineLane(kind)).firstOrNull()

    private fun addOrReplace(timeline: EmployeeTimeline, entry: TimelineEntry): TimelineEdit =
        timeline.entries.firstOrNull { it.kind == entry.kind && it.from == entry.from }
            ?.let { TimelineEdit.Replace(it.id, entry) } ?: TimelineEdit.Add(entry)
}
